import { useState } from 'react';
import { Flag, X } from 'lucide-react';
import { useMutation } from 'react-query';
import { apiClient } from '@/lib/api-client';
import toast from 'react-hot-toast';

type EntityType = 'listing' | 'message' | 'profile' | 'review' | 'forum_post';
type ReportReason = 'spam' | 'scam' | 'harassment' | 'inappropriate' | 'copyright' | 'other';

const REASONS: { value: ReportReason; label: string }[] = [
  { value: 'scam',          label: 'Scam or fraud' },
  { value: 'spam',          label: 'Spam or unwanted advertising' },
  { value: 'harassment',    label: 'Harassment or bullying' },
  { value: 'inappropriate', label: 'Inappropriate content' },
  { value: 'copyright',     label: 'Copyright infringement' },
  { value: 'other',         label: 'Other' },
];

interface Props {
  entityType: EntityType;
  entityId: string;
  reportedUserId: string;
  className?: string;
}

export default function ReportButton({ entityType, entityId, reportedUserId, className = '' }: Props) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<ReportReason | ''>('');
  const [description, setDescription] = useState('');

  const { mutate: submit, isLoading } = useMutation(
    () =>
      apiClient.post('/reports', {
        reported_entity_type: entityType,
        reported_entity_id: entityId,
        reported_user_id: reportedUserId,
        reason,
        description: description.trim() || undefined,
      }),
    {
      onSuccess: () => {
        toast.success('Report submitted. Our team will review it.');
        setOpen(false);
        setReason('');
        setDescription('');
      },
      onError: (err: any) => {
        toast.error(err.response?.data?.error?.message || 'Failed to submit report');
      },
    }
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason) return;
    submit();
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={`inline-flex items-center gap-1.5 text-sm text-gray-500 dark:text-zinc-500 hover:text-red-500 dark:hover:text-red-400 transition-colors ${className}`}
        aria-label="Report this content"
      >
        <Flag className="h-3.5 w-3.5" />
        Report
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-gray-200 dark:border-zinc-800 shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-zinc-800">
              <h2 className="font-bold text-lg text-gray-900 dark:text-zinc-100 flex items-center gap-2">
                <Flag className="h-5 w-5 text-red-500" />
                Report Content
              </h2>
              <button
                onClick={() => setOpen(false)}
                aria-label="Close"
                className="text-gray-400 hover:text-gray-600 dark:text-zinc-500 dark:hover:text-zinc-300"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-2">
                  Reason for reporting <span className="text-red-500">*</span>
                </label>
                <div className="space-y-2">
                  {REASONS.map((r) => (
                    <label key={r.value} className="flex items-center gap-3 cursor-pointer group">
                      <input
                        type="radio"
                        name="reason"
                        value={r.value}
                        checked={reason === r.value}
                        onChange={() => setReason(r.value)}
                        className="text-brand-600 focus:ring-brand-500"
                      />
                      <span className="text-sm text-gray-700 dark:text-zinc-300 group-hover:text-gray-900 dark:group-hover:text-zinc-100">
                        {r.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-1">
                  Additional details <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  maxLength={1000}
                  placeholder="Provide any additional context..."
                  className="input resize-none"
                />
              </div>

              <div className="flex gap-3 pt-1">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="btn-ghost flex-1"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!reason || isLoading}
                  className="btn-danger flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Submitting...' : 'Submit Report'}
                </button>
              </div>

              <p className="text-xs text-gray-400 dark:text-zinc-600 text-center">
                False reports may result in account action.
              </p>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
